import React from 'react';
import {
  Button,
  Counter,
  PaddingModes,
  Text,
  ButtonSize,
} from 'wix-ui-tpa/cssVars';

import { MenuItem } from '../../../../../types/menu';
import type { QuickAddControlContext } from '../../../actions/quickAddToCart';

import { classes, st } from './AddToCartControl.st.css';
import { addToCartControlDataHooks } from './constants';
import { useAddToCartControlHooks } from './useHooks';

interface AddToCartControlProps {
  item: MenuItem;
  menuId: string;
  sectionId: string;
  sectionName?: string;
  currentSectionNumber?: number;
  cartQuantity: number;
  isAvailable: boolean;
  canAcceptOrders?: boolean;
  onQuickAdd: (item: MenuItem, context: QuickAddControlContext) => void;
  onQuickRemove: (item: MenuItem, context: QuickAddControlContext) => void;
  placement: 'itemInfo' | 'onImage';
  inline?: boolean;
  className?: string;
}

export const AddToCartControl = ({
  item,
  menuId,
  sectionId,
  sectionName,
  currentSectionNumber,
  cartQuantity,
  isAvailable,
  canAcceptOrders = true,
  onQuickAdd,
  onQuickRemove,
  placement,
  inline,
  className,
}: AddToCartControlProps) => {
  const {
    isQuickAddToCartEnabled,
    Icon,
    label,
    isStretched,
    isCardLayout,
    showIcon,
    showAddToCartButtonText,
    alignmentName,
    addAriaLabel,
    removeAriaLabel,
    quantityAriaLabel,
  } = useAddToCartControlHooks(placement);

  const handleWrapperClick = (e: React.MouseEvent) => e.stopPropagation();
  const controlContext = {
    menuId,
    sectionId,
    sectionName,
    currentSectionNumber,
    cartQuantity,
    isAvailable,
  };
  const handleAdd = () => onQuickAdd(item, controlContext);
  const handleRemove = () => onQuickRemove(item, controlContext);

  if (!isQuickAddToCartEnabled) {
    return null;
  }

  // The configured alignment only applies to the button's own row — when
  // it's inline, sharing the row with the price, it's always pinned right.
  const effectiveAlignmentName = inline ? 'right' : alignmentName;
  const isIconOnly = showIcon && !showAddToCartButtonText;

  return (
    <div
      className={st(
        classes.wrapper,
        {
          onImage: placement === 'onImage',
          inline: !!inline,
          card: isCardLayout,
          alignment: effectiveAlignmentName,
        },
        className,
      )}
      onClick={handleWrapperClick}
      data-hook={addToCartControlDataHooks.root()}
    >
      {cartQuantity > 0 ? (
        <Counter
          className={classes.stepper}
          data-hook={addToCartControlDataHooks.stepper()}
          decrementAriaLabel={removeAriaLabel}
          incrementAriaLabel={addAriaLabel}
          inputAriaLabel={quantityAriaLabel}
          min={0}
          max={isAvailable && canAcceptOrders ? undefined : cartQuantity}
          value={cartQuantity}
          onChange={(val) => {
            const next = Number(val);

            if (next > cartQuantity) {
              handleAdd();
            } else if (next < cartQuantity) {
              handleRemove();
            }
          }}
        />
      ) : (
        <Button
          upgrade
          paddingMode={isIconOnly ? PaddingModes.explicitPaddings : undefined}
          className={st(classes.control, {
            stretched: isStretched,
            iconOnly: isIconOnly,
          })}
          onClick={handleAdd}
          aria-label={addAriaLabel}
          data-hook={addToCartControlDataHooks.addButton()}
          prefixIcon={
            showIcon && showAddToCartButtonText ? <Icon /> : undefined
          }
          size={ButtonSize.small}
        >
          {showAddToCartButtonText ? (
            <Text
              className={classes.label}
              tagName="span"
              data-hook={addToCartControlDataHooks.label()}
            >
              {label}
            </Text>
          ) : (
            showIcon && <Icon />
          )}
        </Button>
      )}
    </div>
  );
};
